import { v4 as uuidv4 } from 'uuid';

export const TYPES_MAP = {
  campground: 'Campsite',
  permit: 'Permit',
  timedentry_tour: 'Time',
  hotel: 'Room',
  visa_appointment: 'Visa Appointment',
  embassy: 'Embassy',
};

export const INITIAL_STATE = {
  trackerInput: {
    startDate: null,
    endDate: null,
    equipmentType: 'Tent',
    groupSize: 1,
    notificationPref: ['text', 'call'],
    flexiDaysBefore: 0,
    flexiDaysAfter: 0,
    filters: {
      campsites: [],
      accessible: false,
      permitModes: [],
    },
    metadata: {},
  },
  sessionInfo: {
    id: uuidv4(),
    createdAt: new Date(),
  },
  user: {},
  notifications: [],
  location: {
    state: 'Utah',
    city: '',
    country: 'US',
    updated: false,
  },
  testimonials: [],
  promotion: {}
};

export const NAV_LINKS = [
  { title: 'Trip Planner', link: '/plan' },
  { title: 'Where We Serve', link: '/country' },
  { title: 'Blogs', link: '/blogs' },
  { title: 'How It Works', link: '/how-it-works' },
  { title: 'Pricing', link: '/pricing' },
  { title: 'FAQs', link: '/faq' },
  { title: 'Testimonials', link: '/testimonials' },
];

/** Outing types that show flexible dates in checkout; only these can have flexi > 0 in normal flows. */
const FLEXI_ENTITY_TYPES = [
  'campground',
  'hotel',
  'permit',
  'timedentry_tour',
  'tour',
  'embassy',
];

export const TYPE_ENABLED = {
  groupSize: ['hotel', 'campground', 'permit', 'tour'],
  equipmentType: ['campground'],
  accessible: ['campground', 'hotel'],
  specific: ['hotel', 'campground', 'timedentry_tour', 'permit'],
  twoDates: ['campground', 'hotel'],
  flexiDates: FLEXI_ENTITY_TYPES,
};
// These are the top level types that are not selectable unless they don't have any children.
export const NON_SELECTABLE_TYPES = ['recarea', 'visa_appointment', 'tracks'];
// There are not even rendered in the UI.
export const EXCLUDED_CHILD_TYPES = ['ticketfacility', 'timedentry', 'park-lodge', 'tracks'];
export const EXLUDED_ENTITIES = [
  '631cc8cd00fb03e7c9d735d7', // Half Dome Permit
  '631cc8cd00fb03e7c9d73602', // Coyote Buttes North Daily Lottery (The Wave)
  '631cc8cd00fb03e7c9d73601', // Coyote Buttes North Advanced Lottery (The Wave)
];

export const EQUIPMENT_TYPES = {
  'parks-canada-v2': [
    'Small Tent',
    'Medium Tent',
    'Large Tent',
    'Van/Pickup',
    'Tent Trailer',
    'Trailer or Motorhome up to 21ft',
    'Trailer or Motorhome up to 24ft',
    'Trailer or Motorhome up to 27ft',
    'Trailer or Motorhome up to 35ft',
    'Trailer or Motorhome over 35ft',
  ],
  'bc-parks': [
    '1 Tent',
    '2 Tents',
    '3 Tents',
    'Van/Camper',
    'Trailer up to 18ft',
    'Trailer or RV up to 32ft',
    'Trailer or RV over 32ft',
  ],
  // Wisconsin State Parks (Going to Camp — same platform as Parks Canada)
  'wisconsin-sp': [
    'Small Tent',
    'Medium Tent',
    'Large Tent',
    'Van/Pickup',
    'Tent Trailer',
    'Trailer or Motorhome up to 21ft',
    'Trailer or Motorhome up to 24ft',
    'Trailer or Motorhome up to 27ft',
    'Trailer or Motorhome up to 35ft',
    'Trailer or Motorhome over 35ft',
  ],
  // Washington State Parks (Going to Camp)
  'washington-sp': [
    '1 Tent',
    '2 Tents',
    '3 Tents',
    '1 Van/Camper',
    "1 RV/Trailer up to 20'",
    "1 RV/Trailer up to 25'",
    "1 RV/Trailer up to 30'",
    "1 RV/Trailer up to 35'",
    "1 RV/Trailer up to 40'",
    "1 RV/Trailer over 40'",
  ],
  // Ontario Parks (Going to Camp — reservations.ontarioparks.com)
  'ontario-parks': [
    'Single Tent',
    '2 Tents',
    '3 Tents',
    'Trailer or RV up to 18ft (5.5m)',
    'Trailer or RV up to 25ft (7.6m)',
    'Trailer or RV up to 32ft (9.7m)',
    'Trailer or RV over 32ft (9.7m)',
  ],
  // Arizona State Parks (Tyler US eDirect). Cabins are a separate unit type.
  'arizona-sp': ['Tent', 'RV', 'Cabin', 'Day Use', 'Boat-In'],
  default: ['Tent', 'RV'],
};

/** Parks Canada campground and permit trackers share the same equipment list. */
export function getEquipmentTypesForCrawler(crawlerType) {
  if (crawlerType === 'parks-canada-v2-permits') {
    return EQUIPMENT_TYPES['parks-canada-v2'];
  }
  if (crawlerType === 'ontario-parks-permits') {
    return EQUIPMENT_TYPES['ontario-parks'];
  }
  return EQUIPMENT_TYPES[crawlerType] ?? EQUIPMENT_TYPES.default;
}

/**
 * NZ DOC Great Walk huts use campground entity_type; Tent/RV equipment does not apply.
 * Use `source` only: Mongo has parent recareas/tracks with source great-walks but crawlerType recreation.gov.
 */
export function isNzGreatWalksOuting(outing) {
  return outing?.source === 'great-walks';
}

/** Equipment dropdown applies only when TYPE_ENABLED allows it and the outing is not excluded (e.g. NZ huts). */
export function canSelectEquipment(outing) {
  return (
    !!outing &&
    TYPE_ENABLED.equipmentType.includes(outing.entity_type) &&
    !isNzGreatWalksOuting(outing)
  );
}

// export const EQUIPMENT_TYPES = [
//   // 'Boat',
//   // 'Car',
//   // 'Caravan/Camper Van',
//   // 'Fifth Wheel',
//   // 'Horse',
//   // 'Large Tent Over 9X12`',
//   // 'Pickup Camper',
//   // 'Pop up',
//   'RV',
//   // 'RV/Motorhome',
//   // 'Small Tent',
//   'Tent',
//   // 'Trailer',
//   // 'Vehicle',
// ];
export const LOGO_URL_DARK = '/public/reserve-dark.png';
export const LOGO_URL_LIGHT = '/public/reserve-light.png';
export const COUNTRIES = ['US', 'CA', 'NZ', 'AU'];

export const ALL_COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda',
  'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain',
  'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia',
  'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso',
  'Burundi', 'Cambodia', 'Cameroon', 'Canada', 'Cape Verde', 'Central African Republic',
  'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica', 'Croatia',
  'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
  'East Timor', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia',
  'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany',
  'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti',
  'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel',
  'Italy', 'Ivory Coast', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati',
  'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya',
  'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives',
  'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia',
  'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia',
  'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea',
  'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau', 'Panama', 'Papua New Guinea',
  'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia',
  'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa',
  'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles',
  'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia',
  'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname',
  'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Togo',
  'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda',
  'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan',
  'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe',
];

export const CHECKOUT_STAGES = ['review-order', 'payment', 'success'];

export const PAY_AS_YOU_GO_FEATURES = [
  {
    text: 'Allows to create 1 Tracker per use',
    present: true,
  },
  {
    text: 'Checks for availability every 2 mins',
    present: true,
  },
  {
    text: 'Get Call & Text on availability',
    present: true,
  },
  {
    text: 'Add a secondary notification phone',
    present: false,
  },
  {
    text: 'Can book specific campsite/tour time',
    present: true,
  },
  {
    text: 'Allows to choose type of equipment for camping',
    present: false,
  },
  {
    text: 'Flexible dates up to ±2 days',
    present: true,
  },
];

/** Max symmetric flexi days by plan. Recurring subscribers get ±15; pay-as-you-go is ±2. */
export const FLEXI_MAX_PAYG = 2;
export const FLEXI_MAX_SUB = 15;

/**
 * Resolve the max ±flexi days for the current checkout / edit context.
 * Recurring `sub` (ONCE/PAYG is filtered out of `getSub`) → ±15.
 * Selected `pay-as-you-go` → ±2. Other selected plans → ±15.
 * Create flow before a plan is chosen → ±15 (clamped later if they pick PAYG).
 * Edit without a recurring sub → ±2.
 */
export function getMaxFlexiDays({ sub, planSlug, editMode = false } = {}) {
  if (sub?._id) return FLEXI_MAX_SUB;
  if (planSlug === 'pay-as-you-go') return FLEXI_MAX_PAYG;
  if (planSlug) return FLEXI_MAX_SUB;
  if (editMode) return FLEXI_MAX_PAYG;
  return FLEXI_MAX_SUB;
}

/**
 * When checkout plan changes, PAYG clamps flexi to ±2 but remembers the wider
 * window. Monthly/yearly restores that saved window (capped at ±15).
 * Clearing the plan (home / date picker) does not restore — that would fight
 * edit-mode caps and surprise users who never re-picked a plan.
 */
export function flexiForPlanChange({
  slug,
  flexiDaysBefore = 0,
  flexiDaysAfter = 0,
  savedFlexiDays = null,
} = {}) {
  const current = Math.max(
    Number(flexiDaysBefore) || 0,
    Number(flexiDaysAfter) || 0
  );
  if (slug === 'pay-as-you-go') {
    const saved =
      current > FLEXI_MAX_PAYG ? current : savedFlexiDays ?? null;
    const clamped = Math.min(current, FLEXI_MAX_PAYG);
    return {
      flexiDaysBefore: clamped,
      flexiDaysAfter: clamped,
      savedFlexiDays: saved,
    };
  }

  const capAtSubMax = (days) =>
    Math.min(Math.max(0, Number(days) || 0), FLEXI_MAX_SUB);

  // No plan selected: keep current flexi, never restore, always cap at ±15
  // so cap-enforcement effects cannot loop.
  if (!slug) {
    const capped = capAtSubMax(current);
    return {
      flexiDaysBefore: capped,
      flexiDaysAfter: capped,
      savedFlexiDays,
    };
  }

  const source = savedFlexiDays != null ? savedFlexiDays : current;
  const next = capAtSubMax(source);
  return {
    flexiDaysBefore: next,
    flexiDaysAfter: next,
    savedFlexiDays: savedFlexiDays != null ? next : savedFlexiDays,
  };
}

/** Subscription “checks every …” line; {@link getPlanFeatures} uses 30s when `outingPriority === 2`. */
export const BASIC_SUBSCRIPTION_SCAN_INTERVAL_DEFAULT =
  'Checks for availability in less than 90 secs';
export const BASIC_SUBSCRIPTION_SCAN_INTERVAL_30SEC =
  'Checks for availability every 30 secs';

export const BASIC_FEATURES = [
  {
    text: 'Allows to create 5 Trackers per month',
    present: true,
  },
  {
    text: BASIC_SUBSCRIPTION_SCAN_INTERVAL_DEFAULT,
    present: true,
  },
  {
    text: 'Get Call & Text on availability',
    present: true,
  },
  {
    text: 'Add a secondary notification phone',
    present: true,
  },
  {
    text: 'Can book specific campsite/tour time',
    present: true,
  },
  {
    text: 'Allows to choose type of equipment for camping',
    present: true,
  },
  {
    text: 'Flexible dates up to ±15 days',
    present: true,
  },
];

/**
 * Plan feature rows for pricing/checkout. Plans whose map entry uses {@link BASIC_FEATURES} swap the
 * scan-interval bullet for priority-2 outings (30s vs 2m). Other slugs return their map features unchanged.
 */
export function getPlanFeatures({ slug, outingPriority }) {
  const features = PLAN_PROPERTIES_MAP[slug]?.features;
  if (!features) return [];
  // If the plan doesn't use BASIC_FEATURES, return the features unchanged
  if (features !== BASIC_FEATURES) return features;

  const interval =
    Number(outingPriority) === 2
      ? BASIC_SUBSCRIPTION_SCAN_INTERVAL_30SEC
      : BASIC_SUBSCRIPTION_SCAN_INTERVAL_DEFAULT;
  return BASIC_FEATURES.map((f) =>
    f.text === BASIC_SUBSCRIPTION_SCAN_INTERVAL_DEFAULT
      ? { ...f, text: interval }
      : f
  );
}

export const COUNTRY_CURRENCY_MAP = {
  US: 'USD',
  NZ: 'NZD',
  CN: 'CNY',
  JP: 'JPY',
  DE: 'EUR',
  GB: 'GBP',
  IN: 'INR',
  FR: 'EUR',
  IT: 'EUR',
  BR: 'BRL',
  CA: 'CAD',
  KR: 'KRW',
  RU: 'RUB',
  AU: 'AUD',
  ES: 'EUR',
  MX: 'MXN',
  ID: 'IDR',
  NL: 'EUR',
  TR: 'TRY',
  SA: 'SAR',
  CH: 'CHF',
};

export const PLAN_PROPERTIES_MAP = {
  'pay-as-you-go': {
    features: PAY_AS_YOU_GO_FEATURES,
    bgColor: 'dimgray',
    freq: '',
    priority: 'secondary',
    buttonText: 'Select to pay per use',
    upgradeText: 'Upgrade to Monthly Subscription',
    explainText: 'and create 5 trackers per month.',
  },
  'basic-monthly': {
    features: BASIC_FEATURES,
    bgColor: 'green',
    freq: 'month',
    priority: 'primary',
    buttonText: 'Subscribe',
    upgradeText: 'Upgrade to Annual Subscription',
    explainText: 'and save 55%',
  },
  'adventurer-monthly-pass': {
    features: BASIC_FEATURES,
    bgColor: 'green',
    freq: 'month',
    priority: 'primary',
    buttonText: 'Subscribe',
    upgradeText: 'Upgrade to Annual Subscription',
    explainText: 'and save 55%',
  },
  'nature-lovers-yearly-subscription': {
    features: BASIC_FEATURES,
    bgColor: 'green',
    freq: 'year',
    priority: 'primary',
    buttonText: 'Subscribe and Save',
    upgradeText: 'Change Plan',
    explainText: '',
  },
};

// Funnel: pricing page social proof (update or replace with API later)
export const PRICING_SOCIAL_PROOF = {
  alertsLastMonth: 516,
  secondStat: {
    value: '2,500+',
    label: 'Happy campers',
  },
};

export const FONT_FAMILIES = [
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Source Sans Pro',
  'Raleway',
  'Poppins',
  'Ubuntu',
  'Playfair Display',
  'Merriweather',
  'Impact',
];

export const FONT_FAMILIES_SMALL = [
  'roboto',
  'open',
  'lato',
  'montserrat',
  'source',
  'raleway',
  'poppins',
  'ubuntu',
  'playfair',
  'merriweather',
  'impact',
];

export const SUB_STATUS = {
  INACTIVE: 'inactive', // Just created, doesn't get activated until a payment is made
  ACTIVE: 'active',
  PAUSED: 'paused',
  CANCELED: 'canceled',
  PAUSE_REQUESTED: 'pause-requested',
  RESUME_REQUESTED: 'resume-requested',
  CANCEL_REQUESTED: 'cancel-requested',
};

export const TWILIO_FROM = '+1(844)236-6494';

export const STATE_TITLE_MAP = {
  alabama: 'Alabama',
  alaska: 'Alaska',
  arizona: 'Arizona',
  arkansas: 'Arkansas',
  california: 'California',
  colorado: 'Colorado',
  connecticut: 'Connecticut',
  delaware: 'Delaware',
  florida: 'Florida',
  georgia: 'Georgia',
  hawaii: 'Hawaii',
  idaho: 'Idaho',
  illinois: 'Illinois',
  indiana: 'Indiana',
  iowa: 'Iowa',
  kansas: 'Kansas',
  kentucky: 'Kentucky',
  louisiana: 'Louisiana',
  maine: 'Maine',
  maryland: 'Maryland',
  massachusetts: 'Massachusetts',
  michigan: 'Michigan',
  minnesota: 'Minnesota',
  mississippi: 'Mississippi',
  missouri: 'Missouri',
  montana: 'Montana',
  nebraska: 'Nebraska',
  nevada: 'Nevada',
  'new-hampshire': 'New Hampshire',
  'new-jersey': 'New Jersey',
  'new-mexico': 'New Mexico',
  'new-york': 'New York',
  'north-carolina': 'North Carolina',
  'north-dakota': 'North Dakota',
  ohio: 'Ohio',
  oklahoma: 'Oklahoma',
  oregon: 'Oregon',
  pennsylvania: 'Pennsylvania',
  'rhode-island': 'Rhode Island',
  'south-carolina': 'South Carolina',
  'south-dakota': 'South Dakota',
  tennessee: 'Tennessee',
  texas: 'Texas',
  utah: 'Utah',
  vermont: 'Vermont',
  virginia: 'Virginia',
  washington: 'Washington',
  'west-virginia': 'West Virginia',
  wisconsin: 'Wisconsin',
  wyoming: 'Wyoming',
};

export const CA_STATE_TITLE_MAP = {
  alberta: 'Alberta',
  'british-columbia': 'British Columbia',
  manitoba: 'Manitoba',
  'new-brunswick': 'New Brunswick',
  'newfoundland-and-labrador': 'Newfoundland and Labrador',
  'northwest-territories': 'Northwest Territories',
  // nova-scotia: 'Nova Scotia',
  nunavut: 'Nunavut',
  ontario: 'Ontario',
  'prince-edward-island': 'Prince Edward Island',
  quebec: 'Quebec',
  saskatchewan: 'Saskatchewan',
  yukon: 'Yukon',
};

export const NZ_STATE_TITLE_MAP = {
  // northland: 'Northland',
  // auckland: 'Auckland',
  waikato: 'Waikato',
  // 'bay-of-plenty': 'Bay of Plenty',
  // gisborne: 'Gisborne',
  'hawkes-bay': "Hawke's Bay",
  // taranaki: 'Taranaki',
  // wellington: 'Wellington',
  // tasman: 'Tasman',
  nelson: 'Nelson',
  // marlborough: 'Marlborough',
  'west-coast': 'West Coast',
  // canterbury: 'Canterbury',
  otago: 'Otago',
  southland: 'Southland',
  'manawatu-whanganui': 'Manawatū-Whanganui',
};

export const SA_STATE_TITLE_MAP = {
  chile: 'Chile',
};

export const attributes = [
  '<a href="https://www.freepik.com/free-vector/summer-camp-with-bonfire-tent-forest_16646714.htm?query=camping&collectionId=874&&position=2&from_view=collections">Image by upklyak</a> on Freepik',
  '<a href="https://www.freepik.com/free-vector/active-tourist-hiking-mountain-man-wearing-backpack-enjoying-trekking-looking-snowcapped-peaks-vector-illustration-nature-wilderness-adventure-travel-concept_10606146.htm#query=hiking&position=1&from_view=search&track=sph">Image by pch.vector</a> on Freepik',
  '<a href="https://www.freepik.com/free-vector/starry-sky-concept-illustration_37457767.htm#page=2&query=nature&position=7&from_view=search&track=sph">Image by storyset</a> on Freepik',
  'Image by <a href="https://www.freepik.com/free-vector/mountains-landscape-blue-gradient_5418638.htm#page=2&query=nature&position=49&from_view=search&track=sph">Freepik</a>',
  '<a href="https://www.freepik.com/free-vector/glamping-concept-illustration_26233025.htm#page=2&query=camping&position=2&from_view=search&track=sph">Image by storyset</a> on Freepik',
  '<a href="https://www.freepik.com/free-vector/cute-polar-bear-listening-music-cartoon-vector-icon-illustration-animal-technology-icon-concept-isolated-premium-vector-flat-cartoon-style_16306370.htm#query=bear%20call&position=1&from_view=search&track=ais">Image by catalyststuff</a> on Freepik',
];

export const SUPPORTED_COUNTRIES = {
  US: {
    titleMap: STATE_TITLE_MAP,
    name: 'USA',
  },
  CA: {
    titleMap: CA_STATE_TITLE_MAP,
    name: 'Canada',
  },
  NZ: {
    titleMap: NZ_STATE_TITLE_MAP,
    name: 'New Zealand',
  },
  SA: {
    titleMap: SA_STATE_TITLE_MAP,
    name: 'South America',
  },
  AU: {
    name: 'Australia',
  },
};

export const DISCOUNT_CODE = {
  YEAROFF: {
    buttonText: 'Start 1 year free subscription',
    bannerText: '🎉 Claim Free 1 year Subscription! 🎉',
    reviewButtonText: `That's a gift from us `,
    paymentButtonText: 'Start 1 year free subscription',
    successText: 'One year subscription started!',
  },
};

export const getPhoneNumber = (user) => {
  for (const contact of user?.contacts ?? []) {
    if (contact.type === 'phone' && contact.verified === true) {
      return contact.contactId;
    }
  }
  return null;
};